import type { BlueprintToken, RedFlagToken } from '../blueprints/catalog.js';

// ── Condition catalog ────────────────────────────────────────────────────────
// Conditions are the DISPLAY + MEMO layer over the blueprint grammar: reusable
// acquisition-language statements attached to a target, group, build, or
// buyer-fit. They consume the existing evidence spine (canonical scores,
// normalised metrics, mandate/portfolio context) — never a new scoring system,
// never LLM prose. Every rendered condition carries at least one evidence item
// or it must not render at all (spec §8).
// See handover/blueprints/CONDITIONS_PRESCRIPTION_MEMO_SPEC.md.

export type ConditionAppliesTo = 'market_cell' | 'entity' | 'group' | 'build' | 'buyer_fit';

/** The canonical condition shape (spec §4). `note` is evidence-bound and safe
 *  to show verbatim in the UI and the Investment Memo. `tone` is additive to
 *  the spec shape: it says which side of the two-sided thesis the condition
 *  lands on, so the memo can split positives from risks without re-deriving
 *  (`cannibalisation_risk` and value-trap-shaped `quality_upgrade` are risks). */
export interface Condition {
  token: ConditionToken;
  label: string;
  appliesTo: ConditionAppliesTo;
  strength: 'low' | 'moderate' | 'high';
  confidence: 'low' | 'moderate' | 'high';
  tone: 'positive' | 'risk';
  note: string;
  evidence: Array<{ label: string; value: string; source?: string }>;
  caveats: string[];
}

export interface ConditionDef {
  /** Stable machine token. Frozen once shipped. */
  token: string;
  label: string;
  /** One-line meaning shown in Methodology / tooltips. */
  meaning: string;
  /** Which object kinds this condition may attach to. */
  appliesTo: readonly ConditionAppliesTo[];
  /** Blueprint / red-flag tokens this condition is derived from, where the
   *  pattern grammar already covers it (spec §6 — one vocabulary, two layers). */
  fromBlueprints: readonly (BlueprintToken | RedFlagToken)[];
}

export const CONDITIONS = [
  {
    token: 'platform_ready',
    label: 'Platform-ready',
    meaning: 'Scaled, coherent, and credible as a build nucleus.',
    appliesTo: ['entity', 'group', 'build'],
    fromBlueprints: ['platform_anchor', 'buy_and_build_entry', 'consolidation_play'],
  },
  {
    token: 'bolt_on_fit',
    label: 'Bolt-on fit',
    meaning: 'Smaller adjacent add that can plug into an anchor or owned footprint.',
    appliesTo: ['entity', 'buyer_fit', 'build'],
    fromBlueprints: ['bolt_on'],
  },
  {
    token: 'local_champion',
    label: 'Local champion',
    meaning: 'Regionally meaningful operator despite not being nationally large.',
    appliesTo: ['entity', 'group'],
    fromBlueprints: [],
  },
  {
    token: 'hidden_platform',
    label: 'Hidden platform',
    meaning:
      'Multi-site or multi-entity operator behaving like a platform before being labelled one.',
    appliesTo: ['group', 'entity'],
    fromBlueprints: [],
  },
  {
    token: 'specialism_scarce',
    label: 'Specialism scarce',
    meaning: 'Carries a clinical/revenue specialism under-supplied relative to local demand.',
    appliesTo: ['entity', 'group', 'market_cell'],
    fromBlueprints: ['specialism_scarcity'],
  },
  {
    token: 'capacity_constrained',
    label: 'Capacity constrained',
    meaning: 'Demand/capacity evidence suggests supply is tight.',
    appliesTo: ['market_cell', 'build'],
    fromBlueprints: [],
  },
  {
    token: 'quality_upgrade',
    label: 'Quality upgrade',
    meaning: 'Professionalisation/improvement opportunity, without asserting distress.',
    appliesTo: ['entity', 'group'],
    fromBlueprints: ['quality_arbitrage', 'distress_turnaround'],
  },
  {
    token: 'succession_rich',
    label: 'Succession rich',
    meaning: 'Seller-readiness / owner-transition signals are unusually strong.',
    appliesTo: ['market_cell', 'entity'],
    fromBlueprints: ['succession_sale'],
  },
  {
    token: 'footprint_gap',
    label: 'Footprint gap',
    meaning: 'Target extends the buyer into a missing geography/cell.',
    appliesTo: ['buyer_fit', 'build'],
    fromBlueprints: ['footprint_gap_filler'],
  },
  {
    token: 'cannibalisation_risk',
    label: 'Cannibalisation risk',
    meaning: 'Target overlaps owned footprint/catchment and may add cost rather than reach.',
    appliesTo: ['buyer_fit', 'build'],
    fromBlueprints: ['cannibalisation'],
  },
] as const satisfies ReadonlyArray<ConditionDef>;

export type ConditionToken = (typeof CONDITIONS)[number]['token'];

export const CONDITION_LABEL = Object.fromEntries(
  CONDITIONS.map((c) => [c.token, c.label]),
) as Record<ConditionToken, string>;

const CONDITION_APPLIES = Object.fromEntries(
  CONDITIONS.map((c): [ConditionToken, readonly ConditionAppliesTo[]] => [c.token, c.appliesTo]),
) as Record<ConditionToken, readonly ConditionAppliesTo[]>;

/** Guard used by the engine so a condition can never be minted against an
 *  object kind its definition doesn't allow. */
export function conditionAllows(token: ConditionToken, appliesTo: ConditionAppliesTo): boolean {
  return CONDITION_APPLIES[token].includes(appliesTo);
}
